import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Zap, Brain, Loader2, Lock } from "lucide-react";
import { reclassifyArchivedBatch } from "./api";
import { toast } from "sonner";

/* Re-classify modal — two options:
   - Rules Only (instant, no AI)
   - Rules + AI (slower, smarter)
   Both skip locked manual allocations and committed records. */
export default function ReclassifyModal({ batchId, open, onClose, onComplete }) {
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (chosenMode) => {
    setMode(chosenMode);
    setBusy(true);
    try {
      const res = await reclassifyArchivedBatch(batchId, chosenMode);
      const d = res.data || {};
      // Build a clear toast describing what happened.
      const lines = [];
      if (chosenMode === "rules_only") {
        lines.push(
          `${d.allocated || 0} confident · ${d.needs_review || 0} flagged for review`
        );
      } else {
        lines.push(`Reclassifying ${d.reclassified || 0} records with rules + AI`);
      }
      if (d.locked_skipped) lines.push(`${d.locked_skipped} locked skipped`);
      if (d.committed_skipped) lines.push(`${d.committed_skipped} already committed`);
      toast.success(d.message || "Reclassification started", {
        description: lines.join(" · "),
      });
      onComplete?.(d);
      onClose();
    } catch (e) {
      toast.error("Reclassify failed", {
        description: e.response?.data?.detail || e.message || "Unknown error",
      });
    } finally {
      setBusy(false);
      setMode(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="bg-slate-950 border-slate-800 max-w-md" data-testid="reclassify-modal">
        <DialogHeader>
          <DialogTitle className="text-slate-100 text-base">Re-classify Batch</DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">
            How should we re-classify these records?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2.5 py-2">
          <button
            disabled={busy}
            onClick={() => submit("rules_only")}
            className="w-full text-left rounded-lg border border-emerald-500/30 bg-emerald-500/[0.06] hover:bg-emerald-500/[0.12] transition-colors p-3 disabled:opacity-50"
            data-testid="reclassify-mode-rules"
          >
            <div className="flex items-center gap-2 mb-1">
              {busy && mode === "rules_only" ? (
                <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 text-emerald-400" />
              )}
              <span className="text-sm font-semibold text-emerald-300">
                Rules Only — Instant
              </span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Re-runs the deterministic keyword engine. Completes in under two
              seconds. No AI calls.
            </p>
          </button>

          <button
            disabled={busy}
            onClick={() => submit("rules_plus_ai")}
            className="w-full text-left rounded-lg border border-cyan-500/30 bg-cyan-500/[0.06] hover:bg-cyan-500/[0.12] transition-colors p-3 disabled:opacity-50"
            data-testid="reclassify-mode-ai"
          >
            <div className="flex items-center gap-2 mb-1">
              {busy && mode === "rules_plus_ai" ? (
                <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
              ) : (
                <Brain className="w-4 h-4 text-cyan-400" />
              )}
              <span className="text-sm font-semibold text-cyan-300">
                Rules + AI — More Accurate
              </span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Rules first, then AI on the leftovers. Slower, but catches
              ambiguous descriptions the rules miss.
            </p>
          </button>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-slate-800/60 bg-slate-900/40 px-3 py-2">
          <Lock className="w-3 h-3 text-slate-500 mt-0.5 shrink-0" />
          <p className="text-[10px] text-slate-500 leading-relaxed">
            Locked manual allocations and already-committed records will not
            be changed.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={busy}
            data-testid="reclassify-cancel-btn"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
